import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { Form, FormActions, FormInput, FormSelect } from '../../../components/form/index.js';
import { useUsersContext } from '../../../contexts/users.tsx';
import { BodyMeasurements } from '../../../models/users/bodyMeasurements.ts';
import { Color } from '../../../models/helpers/colors.ts';

const validationSchema = Yup.object().shape({
    height: Yup
        .number()
        .required('Height is required')
        .typeError('Height must be a number')
        .min(0, "Height must be a positive number"),
    weight: Yup
        .number()
        .required('Weight is required')
        .typeError('Weight must be a number')
        .min(0, "Weight must be a positive number"),
    bodyFatPercentage: Yup
        .number()
        .nullable()
        .typeError('Body fat percentage must be a number')
        .min(0, "Body fat percentage must be a positive number"),
    leanBodyMass: Yup
        .number()
        .nullable()
        .typeError('Lean body mass must be a number')
        .min(0, "Lean body mass must be a positive number"),
    waistCircumference: Yup
        .number()
        .nullable()
        .typeError('Waist circumference must be a number')
        .min(0, "Waist circumference must be a positive number"),
    activityLevel: Yup
        .number()
        .nullable()
        .typeError('Activity level must be a number')
        .min(0, "Activity level must be a positive number"),
});

const activityLevelOptions = [
    { name: 'Sedentary (Under 5000 steps daily)', activityLevel: 0 },
    { name: 'Less active (Between 5000 and 10000 steps daily)', activityLevel: 1 },
    { name: 'Active (Over 10000 steps daily)', activityLevel: 2 },
    { name: 'Very active (Over 12500 steps daily)', activityLevel: 3 },
]

const MeasurementsBody = () => {
    const { profile, fetchProfile, insertBodyMeasurements } = useUsersContext();
    const [localBodyMeasurements, setLocalBodyMeasurements] = useState<BodyMeasurements>({} as BodyMeasurements);
    const [calculatedBodyMeasurements, setCalculatedBodyMeasurements] = useState<any>({});
    useEffect(() => {
        if (!profile || profile == null)
            fetchProfile();
        if (profile.bodyMeasurements) {
            setLocalBodyMeasurements(profile.bodyMeasurements);

            if (profile.bodyMeasurements.weight == null || profile.bodyMeasurements.height == null)
                return;
            calculatedBodyMeasurements.bmi = profile.bodyMeasurements.weight / (profile.bodyMeasurements.height / 100 * profile.bodyMeasurements.height / 100);

            if (profile.birthDate == null)
                return;
            const ageDifMs = Date.now() - new Date(profile.birthDate).getTime();
            const ageDate = new Date(ageDifMs);
            const age = Math.abs(ageDate.getUTCFullYear() - 1970);
            calculatedBodyMeasurements.age = age;

            if (profile.bodyMeasurements.leanBodyMass)
                calculatedBodyMeasurements.bmr = 370 + (21.6 * profile.bodyMeasurements.leanBodyMass)
            else if (profile.sex == 'M')
                calculatedBodyMeasurements.bmr = 10 * profile.bodyMeasurements.weight + 6.25 * profile.bodyMeasurements.height - 5 * calculatedBodyMeasurements.age + 5
            else if (profile.sex == 'F')
                calculatedBodyMeasurements.bmr = 10 * profile.bodyMeasurements.weight + 6.25 * profile.bodyMeasurements.height - 5 * calculatedBodyMeasurements.age - 161

            if (profile.bodyMeasurements.activityLevel == null)
                return;

            if (profile.bodyMeasurements.activityLevel == 0)
                calculatedBodyMeasurements.tdee = calculatedBodyMeasurements.bmr * 1.2
            if (profile.bodyMeasurements.activityLevel == 1)
                calculatedBodyMeasurements.tdee = calculatedBodyMeasurements.bmr * 1.375
            if (profile.bodyMeasurements.activityLevel == 2)
                calculatedBodyMeasurements.tdee = calculatedBodyMeasurements.bmr * 1.55
            if (profile.bodyMeasurements.activityLevel == 3)
                calculatedBodyMeasurements.tdee = calculatedBodyMeasurements.bmr * 1.725

            setCalculatedBodyMeasurements(calculatedBodyMeasurements);
        }
    }, [profile])
    const [errors, setErrors] = useState<Record<string, string>>({});

    const handleInputChange = (e: any) => {
        const { id, value } = e.target;
        setLocalBodyMeasurements((prevData) => ({
            ...prevData,
            [id]: value, // Update the corresponding field in state
        }));

        validationSchema
            .validateAt(id, { ...localBodyMeasurements, [id]: value })
            .then(() => setErrors((prev) => ({ ...prev, [id]: '' })))
            .catch((err) => setErrors((prev) => ({ ...prev, [id]: err.message })));
    };

    const handleSubmit = () => {
        validationSchema
            .validate(localBodyMeasurements, { abortEarly: false })
            .then(() => {
                insertBodyMeasurements(localBodyMeasurements);
            })
            .catch((err) => {
                const validationErrors: Record<string, string> = {};
                err.inner.forEach((error: Yup.ValidationError) => {
                    if (error.path) validationErrors[error.path] = error.message;
                });
                setErrors(validationErrors);
            });
    };
    return (
        <Form>
            <FormInput
                entity={localBodyMeasurements}
                handleInputChange={handleInputChange}
                fieldName='height'
                label='Height'
                errors={errors}
            />
            <FormInput
                entity={localBodyMeasurements}
                handleInputChange={handleInputChange}
                fieldName='weight'
                label='Weight'
                errors={errors}
            />
            <FormInput
                entity={localBodyMeasurements}
                handleInputChange={handleInputChange}
                fieldName='bodyFatPercentage'
                label='Body Fat Percentage'
                errors={errors}
            />
            <FormInput
                entity={localBodyMeasurements}
                handleInputChange={handleInputChange}
                fieldName='leanBodyMass'
                label='Lean Body Mass'
                errors={errors}
            />
            <FormInput
                entity={localBodyMeasurements}
                handleInputChange={handleInputChange}
                fieldName='waistCircumference'
                label='Waist Circumference'
                errors={errors}
            />
            <FormSelect
                entity={localBodyMeasurements}
                handleInputChange={handleInputChange}
                options={activityLevelOptions}
                fieldName='activityLevel'
                label='Activity Level'
                errors={errors}
            />
            <FormInput
                entity={calculatedBodyMeasurements}
                handleInputChange={handleInputChange}
                fieldName='bmi'
                label='BMI'
                type='decimal'
                toFixed={2}
                append='kg/m2'
                errors={calculatedBodyMeasurements.bmi == null ? { bmi: 'BMI is calculated with Weight and Height' } : {}}
                errorColor={Color.warning}
                disabled
            />
            <FormInput
                entity={calculatedBodyMeasurements}
                handleInputChange={handleInputChange}
                fieldName='bmr'
                label='BMR'
                type='decimal'
                toFixed={2}
                append='kCal'
                errors={calculatedBodyMeasurements.bmr == null ? { bmr: 'BMR is calculated with Lean Body Mass, Height, Weight and Age' } : {}}
                errorColor={Color.warning}
                disabled
            />
            <FormInput
                entity={calculatedBodyMeasurements}
                handleInputChange={handleInputChange}
                fieldName='tdee'
                label='TDEE'
                type='decimal'
                toFixed={2}
                append='kCal'
                errors={calculatedBodyMeasurements.tdee == null ? { tdee: 'TDEE is calculated with BMR and Activity Level' } : {}}
                errorColor={Color.warning}
                disabled
            />
            <FormActions
                mainAction={handleSubmit}
                mainActionText={"Save"}
                showCancelButton
            />
        </Form>
    );
};

export default MeasurementsBody;